import { Link } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { POSTS } from "../content/posts";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SearchDialog({ open, onClose }: Props) {
  const [q, setQ] = useState("");
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [mounted, setMounted] = useState(open);

  const query = q.trim().toLowerCase();
  const results = useMemo(() => {
    if (!query) return POSTS.slice(0, 6);
    return POSTS.filter((p) => {
      const m = p.meta;
      const hay = [m.title, m.description, m.category, m.heroEyebrow, ...(m.tags ?? [])]
        .join(" ")
        .toLowerCase();
      return hay.includes(query);
    }).slice(0, 12);
  }, [query]);

  // Mount/unmount with CSS transitions
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!mounted) return;
    if (open) {
      const raf = requestAnimationFrame(() => {
        setVisible(true);
        inputRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [open, mounted]);


  // Backdrop is driven by a CSS class on <html>, not inline styles, so it
  // can never get stuck. Add on open, remove the instant open flips false
  // (before the close animation finishes) and on unmount. Also scrub any
  // legacy inline styles a previous build may have left on #site-root.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    const siteRoot = document.getElementById("site-root");
    if (siteRoot && (siteRoot.style.opacity || siteRoot.style.filter)) {
      siteRoot.style.opacity = "";
      siteRoot.style.filter = "";
    }
    if (open) {
      html.classList.add("search-open");
    } else {
      html.classList.remove("search-open");
    }
    return () => {
      html.classList.remove("search-open");
      if (siteRoot) {
        siteRoot.style.opacity = "";
        siteRoot.style.filter = "";
      }
    };
  }, [open]);

  // Esc to close, body scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Reset query when closed
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  if (!mounted) return null;

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-[60] flex items-start justify-center bg-ink/40 px-4 pt-[10vh] transition-opacity duration-200 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Search the archive"
    >
      <div
        ref={panelRef}
        className={`w-full max-w-2xl bg-paper border border-ink shadow-2xl transition-all duration-300 ease-out ${
          visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-3 scale-[0.98]"
        }`}
      >

        <div className="flex items-center gap-3 border-b border-rule px-4 py-3">
          <Search size={18} className="text-pencil" />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search notes, posts, categories..."
            className="flex-1 bg-transparent text-lg text-ink placeholder:text-pencil/60 outline-none"
            aria-label="Search query"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            className="p-1 text-pencil hover:text-grade transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-4 py-10 text-center text-pencil">
              Nothing matches "{q}". Try a shorter word.
            </div>
          ) : (
            <ul className="divide-y divide-rule">
              {!query && (
                <li className="px-4 pt-3 mono-label text-pencil">Latest</li>
              )}
              {results.map((p) => (
                <li key={p.meta.slug}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.meta.slug }}
                    onClick={onClose}
                    className="block px-4 py-3 hover:bg-ink/5 transition-colors"
                  >
                    <div className="mono-label text-grade">{p.meta.category}</div>
                    <div
                      className="mt-1 text-base font-medium text-ink"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      {p.meta.title}
                    </div>
                    <div className="mt-1 text-sm text-pencil line-clamp-1">
                      {p.meta.description}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-rule px-4 py-2 flex items-center justify-between mono-label text-pencil">
          <span>{query ? `${results.length} result${results.length === 1 ? "" : "s"}` : "Start typing"}</span>
          <span>Esc to close</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}